import React, { useEffect, useRef, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material"
import PointLogo from "../../assets/icons/point.svg"
import Linkedin from "../../assets/icons/linkedin-news.svg"
import Twitter from "../../assets/icons/twitter-news.svg"
import Share from "../../assets/icons/share-news.svg"
import CustomInputFilter from "../../components/custom-input-filter/custom-input-filter.component"
import CustomButton from "../../components/custom-button/custom-button.component"
import useDebounce from "../../hooks/useDebounce"

import * as S from "./news.styles"
import CustomLink from "../../components/custom-link/custom-link.component"
import { toast } from "react-toastify"
import { isBrowser } from "../../utils/utils"

const LIMIT = 6

const News = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [searchValue, setSearchValue] = useState("")
  const [categorie, setCategorie] = useState("all")
  const debouncedSearchValue = useDebounce(searchValue, 500)
  const [visible, setVisible] = useState(LIMIT)

  const staticQuery = useStaticQuery(graphql`
    {
      allWpNew(sort: { date: DESC }) {
        nodes {
          date(formatString: "MMMM Do, YYYY")
          featuredImage {
            node {
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH, quality: 100)
                }
              }
              altText
              title
            }
          }
          newsFields {
            url
          }
          title
          content
          slug
          newsCategories {
            nodes {
              name
            }
          }
        }
      }

      allWpNewsCategory {
        nodes {
          name
        }
      }
    }
  `)

  // console.log("staticQueryCategories", staticQuery.allWpNew.nodes)
  const news = staticQuery?.allWpNew?.nodes
  const categories = staticQuery?.allWpNewsCategory?.nodes
  const [filterNews, setFilterNews] = useState(news)

  const handleCopy = url => {
    // console.log("url", url)
    window.navigator.clipboard.writeText(url)
    toast.success("Copied to clipboard")
  }

  useEffect(() => {
    // console.log(
    //   news?.filter(({ title }) =>
    //     title.toLowerCase().includes(searchValue.toLowerCase()),
    //   ),
    // )
    if (categorie !== "all") {
      setFilterNews(
        news?.filter(
          ({ title, newsCategories }) =>
            title.toLowerCase().includes(searchValue.toLowerCase()) &&
            newsCategories?.nodes[0]?.name.toLowerCase() ===
              categorie.toLowerCase(),
        ),
      )
    } else {
      setFilterNews(
        news?.filter(({ title }) =>
          title.toLowerCase().includes(searchValue.toLowerCase()),
        ),
      )
    }
  }, [debouncedSearchValue, categorie])

  const showMoreItems = () => {
    setVisible(prevValue => prevValue + LIMIT)
  }
  const handleClick = social => {
    if (isBrowser && window.gtag) {
      window.gtag("event", `${social}_share`)
    }
  }

  return (
    <S.Section>
      <S.Wrapper maxWidth="xl">
        <Grid
          container
          spacing={2}
          direction={isMobile ? "column-reverse" : "row"}
        >
          <Grid item container xs={12} md={8} spacing={2}>
            {filterNews?.length > 0 ? (
              <>
                {filterNews
                  ?.slice(0, visible)
                  ?.map(
                    (
                      {
                        title,
                        featuredImage,
                        newsCategories,
                        date,
                        newsFields,
                        content,
                        slug,
                      },
                      index,
                    ) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        key={`${index}-${title}`}
                      >
                        <S.CardContainer>
                          <CustomLink
                            url={newsFields?.url ?? `/news/${slug}`}
                            target="_blank"
                          >
                            <S.Image img={featuredImage?.node} />
                          </CustomLink>
                          <CustomLink
                            url={newsFields?.url ?? `/news/${slug}`}
                            target="_blank"
                          >
                            <S.DescriptionContainer>
                              <S.Description>
                                <span>{newsCategories?.nodes[0]?.name}</span>
                                <PointLogo />
                                <Typography>{date}</Typography>
                              </S.Description>
                              <S.Title>{title}</S.Title>
                            </S.DescriptionContainer>
                          </CustomLink>
                          <S.ShareContainer className="buttons-container">
                            <Typography>Share via</Typography>
                            <S.ButtonsContainer>
                              <S.Button
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${
                                  newsFields?.url ??
                                  `https://gocopia.com/news/${slug}`
                                }`}
                                target="_blank"
                                onClick={() => handleClick("linkedin")}
                              >
                                <Linkedin />
                              </S.Button>
                              <S.Button
                                href={`https://twitter.com/intent/tweet?url=${
                                  newsFields?.url ??
                                  `https://gocopia.com/news/${slug}`
                                }`}
                                target="_blank"
                                onClick={() => handleClick("x")}
                              >
                                <Twitter />
                              </S.Button>
                              <S.Button
                                onClick={() =>
                                  handleCopy(
                                    newsFields?.url ??
                                      `https://gocopia.com/news/${slug}`,
                                  )
                                }
                              >
                                <Share />
                              </S.Button>
                            </S.ButtonsContainer>
                          </S.ShareContainer>
                        </S.CardContainer>
                      </Grid>
                    ),
                  )}

                {filterNews?.length > LIMIT && filterNews?.length > visible && (
                  <S.LoadMoreButtonContainer>
                    <CustomButton
                      className="lightBorder"
                      onClick={showMoreItems}
                      style={{ height: "52px" }}
                    >
                      Load More
                    </CustomButton>
                  </S.LoadMoreButtonContainer>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <S.Message>No results found</S.Message>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <S.SearchContainer>
              <S.Label>Search News</S.Label>
              <CustomInputFilter
                name="Search by keywords"
                setValue={setSearchValue}
              />
            </S.SearchContainer>
            {!isMobile && (
              <S.CategoriesContainer>
                <S.Label>Categories</S.Label>
                <S.Categorie
                  onClick={() => setCategorie("all")}
                  className={categorie === "all" ? "active" : ""}
                >
                  All
                </S.Categorie>
                {categories?.map(({ name }, index) => (
                  <S.Categorie
                    key={`${index}-${name}`}
                    onClick={() => setCategorie(name)}
                    className={categorie === name ? "active" : ""}
                  >
                    {name}
                  </S.Categorie>
                ))}
              </S.CategoriesContainer>
            )}
          </Grid>
        </Grid>
      </S.Wrapper>
    </S.Section>
  )
}

export default News
